<template>
  <Transition name="slide-fade">
    <div
      v-show="isShow"
      class="text-sm/[150%] sm:text-base/[150%] bg-semantic-surface-on-surface text-semantic-inverse-on-surface p-4 w-full sm:exact-w-[22.75rem] rounded-xl shadow-elevation-3 pointer-events-auto gap-3 items-start flex"
      :class="{
        'items-center': layout !== 'long cta',
        'flex-wrap': layout === 'long cta'
      }"
      v-bind="$attrs"
    >
      <component
        :is="getKey(type, componentMap)"
        v-if="hasIcon"
        width="24"
        height="24"
        class="exact-wh-6"
        :class="[
          component.order.icon
        ]"
        :fill="semanticColorLookup('inverse.on-surface')"
        :type="component.iconType"
      />

      <!-- eslint-disable vue/no-v-html -->
      <p
        :class="[
          component.order.text,
          component.textWidth,
          {
            'flex-1': layout !== 'long cta'
          }
        ]"
        v-html="text"
      >
      </p>

      <span
        v-if="layout && layout !== 'text'"
        class="text-semantic-inverse-primary font-semibold text-base cursor-pointer"
        :class="[
          component.order.action,
          {
            'block ml-auto': layout === 'long cta',
          }
        ]"
        @click="ctaCallback"
      >
        {{ ctaText }}
      </span>

      <V2IconsClose
        v-if="hasCloseButton"
        width="24"
        height="24"
        class="exact-wh-6 cursor-pointer"
        :class="[
          component.order.close
        ]"
        :fill="semanticColorLookup('inverse.on-surface')"
        @click="handleClose($attrs.id)"
      />
    </div>
  </Transition>
</template>

<script setup>
defineOptions({
  name: 'MoleculesSnackbar',
  inheritAttrs: false
})

const { $snackbar } = useNuxtApp()

const $attrs = useAttrs()

const props = defineProps({
  isShow: {
    type: Boolean,
    default: false
  },

  text: {
    type: String,
    default: ''
  },

  type: {
    type: String,
    default: 'success',
    validator: value => ['success', 'error'].includes(value)
  },

  layout: {
    type: String,
    default: 'text',
    validator: value => ['text', 'short cta', 'long cta'].includes(value)
  },

  hasIcon: {
    type: Boolean,
    default: false
  },

  hasCloseButton: {
    type: Boolean,
    default: false
  },

  ctaText: {
    type: String,
    default: 'Action'
  },

  ctaCallback: {
    type: Function,
    default: () => ({})
  }
})

const componentMap = {
  'success': resolveComponent('V2IconsCheckCircle'),
  'error': resolveComponent('V2IconsClose')
}

const component = computed(() => { 
  const order = {
    icon: 'order-1',
    text: 'order-2',
    action: getKey(props.layout, {
      default: 'order-3',
      'long cta': 'order-4'
    }),
    close: getKey(props.layout, {
      default: 'order-4',
      'long cta': 'order-3'
    }) 
  }

  const iconType = getKey(props.type, {
    success: 'default',
    error: 'circle'
  })

  const textWidth = (() => { 
    if (props.layout !== 'long cta') {
      return ''
    }

    if (props.hasIcon && props.hasCloseButton) {
      return 'w-[calc(100%-(24px*2)-(12px*2))]'
    }

    if (props.hasIcon && !props.hasCloseButton) {
      return 'w-[calc(100%-24px-12px)]'
    }

    if (!props.hasIcon && props.hasCloseButton) {
      return 'w-[calc(100%-24px-12px)]'
    } 

    return ''
  })()

  return {
    order,
    iconType,
    textWidth
  }
})

function handleClose (id) {
  $snackbar.remove(id)
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.25s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}
</style>